import { QuestionFormSchemaType } from "@/features/question/components/organisms/QuestionForm";
import { exportSurveyToAirtable } from "@/lib/export/crm/airtable/exportSurveyToAirtable";
import { UseFormReturn } from "react-hook-form";
import { CrmSystem } from "../../_core/crmSystem";
import { BooleanForm, CheckboxForm, CurrencyForm, NoOptionsForm, NumberForm, TextForm } from "../_common/FieldForm";
import { BooleanRenderer, CheckboxRenderer, CurrencyRenderer, EmailRenderer, NumberRenderer, RadioRenderer, TextRenderer, TextareaRenderer } from "../_common/FieldRenderer";
import { booleanParser, dateParser, listParser, numberParser, percentParser, stringParser } from "../_common/useGetExportData/parser/_core";
import { FetchAirtableDataStep, FetchAirtableDataStepTitle } from "./AddCrmModalLimited/FetchAirtableDataStep";
import { AirtableConfigurationParameterInput } from "./ConfigurationParameterInputResolver";
import { DateRenderer, DurationHoursRenderer, RatingRenderer } from "./FieldRenderer";
import { DateTimeForm, DurationForm, RatingForm } from "./FieldsForm";
import { DateForm } from "./FieldsForm/DateForm";
import { DurationDaysForm } from "./FieldsForm/DurationDays";
import { dateOnlyParser, durationParser } from "./useGetExportData";

class AirtableCrmSystem extends CrmSystem<'airtable'> {
    id = "airtable" as const;
    name = "Airtable";
    exportHandler = exportSurveyToAirtable;
    configurationParameterInput = AirtableConfigurationParameterInput;
    notSupportedFieldTypes = ['singleCollaborator','multipleCollaborators' ,'multipleRecordLinks', 'multipleAttachments', 'formula', 'createdTime', 'rollup', 'count', 'lookup', 'multipleLookupValues','autoNumber', 'barcode', 'richText', 'lastModifiedTime', 'button', 'createdBy', 'lastModifiedBy', 'externalSyncSource', 'aiText'];
    branding = {
        color: '#ffba05',
        logoUrl: 'https://cdn.solved.app/airtable-logo.png'
    };
    fieldTypes = {
        'singleLineText': {
            formComponent: TextForm,
            rendererComponent: TextRenderer,
            name: 'Single Line Text',
            id: 'singleLineText',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'text',
                'collaborate': 'text'
            },
            baseType: 'text',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form?.setValue("field_min", 0);
                form?.setValue("field_max", null);
            }
        } as const,
        'multilineText': {
            formComponent: TextForm,
            rendererComponent: TextareaRenderer,
            name: 'Multiline Text',
            id: 'multilineText',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'textarea',
                'collaborate': 'textarea'
            },
            baseType: 'text',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form?.setValue("field_min", 0);
                form?.setValue("field_max", null);
            }
        } as const,

        'number': {
            formComponent: NumberForm,
            rendererComponent: NumberRenderer,
            name: 'Number',
            id: 'number',
            parser: numberParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'number',
                'collaborate': 'number'
            },
            baseType: 'number'
        } as const,
        'currency': {
            formComponent: CurrencyForm,
            rendererComponent: CurrencyRenderer,
            name: 'Currency',
            id: 'currency',
            parser: numberParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'currency',
                'collaborate': 'currency'
            },
            baseType: 'number'
        } as const,
        'percent': {
            formComponent: NumberForm,
            rendererComponent: NumberRenderer,
            name: 'Percent',
            id: 'percent',
            parser: percentParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'number',
                'collaborate': 'number'
            },
            baseType: 'number',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form?.setValue("field_min", 0);
                form?.setValue("field_max", 100);
                form?.setValue("field_integer_places", 2);
                form?.setValue("field_decimal_places", 2);
            }
        } as const,
        'rating': {
            formComponent: RatingForm,
            rendererComponent: RatingRenderer,
            name: 'Rating',
            id: 'rating',
            parser: numberParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'number',
                'collaborate': 'number'
            },
            baseType: 'number'
        } as const,
        'singleSelect': {
            formComponent: CheckboxForm,
            rendererComponent: RadioRenderer,
            name: 'Single Select',
            id: 'singleSelect',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'radio',
                'collaborate': 'radio'
            },
            baseType: 'text',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form?.setValue("field_options", []);
            }
        } as const,
        'multipleSelects': {
            formComponent: CheckboxForm,
            rendererComponent: CheckboxRenderer,
            name: 'Multiple Selects',
            id: 'multipleSelects',
            parser: listParser(','),
            otherCrmSystemEquivalent: {
                'salesforce': 'checkbox',
                'collaborate': 'checkbox'
            },
            baseType: 'list',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form?.setValue("field_options", []);
            }
        } as const,
        'checkbox': {
            formComponent: BooleanForm,
            rendererComponent: BooleanRenderer,
            name: 'Checkbox',
            id: 'checkbox',
            parser: booleanParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'boolean',
                'collaborate': 'boolean'
            },
            baseType: 'boolean',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form?.setValue("field_min_label", "No");
                form?.setValue("field_max_label", "Yes");
            }
        } as const,
        'date': {
            formComponent: DateForm,
            rendererComponent: DateRenderer,
            name: 'Date',
            id: 'date',
            parser: dateOnlyParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'date',
                'collaborate': 'date'
            },
            baseType: 'number',

        } as const,
        'dateTime': {
            formComponent: DateForm,
            rendererComponent: DateRenderer,
            name: 'Date Time',
            id: 'dateTime',
            parser: dateParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'date',
                'collaborate': 'date'
            },
            baseType: 'number',
            resetForm: (form: UseFormReturn<QuestionFormSchemaType>) => {
                form?.setValue("field_date_options", {
                    year_only: false,
                    timezone: "client",
                    timeFormat: 'h:mma',
                });
            }
        } as const,
        'duration_hours': {
            formComponent: DurationForm,
            rendererComponent: DurationHoursRenderer,
            name: 'Duration (Hours)',
            id: 'duration_hours',
            parser: durationParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'date',
                'collaborate': 'date'
            },
            baseType: 'number'
        } as const,
        'duration_days': {
            formComponent: DurationDaysForm,
            rendererComponent: NumberRenderer,
            name: 'Duration (Days)',
            id: 'duration_days',
            parser: dateOnlyParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'date',
                'collaborate': 'date'
            },
            baseType: 'number'
        } as const,
        'dateTimezone': {
            formComponent: DateTimeForm,
            rendererComponent: DateRenderer,
            name: 'Date with Timezone',
            id: 'dateTimezone',
            parser: dateOnlyParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'date',
                'collaborate': 'date'
            },
            baseType: 'number'
        } as const,
        'email': {
            formComponent: NoOptionsForm,
            rendererComponent: EmailRenderer,
            name: 'Email',
            id: 'email',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'text',
                'collaborate': 'text'
            },
            baseType: 'text'
        } as const,
        'phoneNumber': {
            formComponent: NoOptionsForm,
            rendererComponent: TextRenderer,
            name: 'Phone Number',
            id: 'phoneNumber',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'text',
                'collaborate': 'text'
            },
            baseType: 'text'
        } as const,
        'url': {
            formComponent: NoOptionsForm,
            rendererComponent: TextRenderer,
            name: 'URL',
            id: 'url',
            parser: stringParser,
            otherCrmSystemEquivalent: {
                'salesforce': 'text',
                'collaborate': 'text'
            },
            baseType: 'text'
        } as const,


    };
    resetForm(
        field: keyof typeof this.fieldTypes,
        form: UseFormReturn<QuestionFormSchemaType>
    ) {
        switch (field) {
            case "checkbox": {
                form?.setValue("field_min_label", "No");
                form?.setValue("field_max_label", "Yes");
                break;
            }
            case "singleLineText":
            case "multilineText": {
                form?.setValue("field_min", 0);
                form?.setValue("field_max", null);
                break;
            }
            case "singleSelect":
            case "multipleSelects": {
                form?.setValue("field_options", []);
                break;
            }
            case "percent": {
                form?.setValue("field_min", 0);
                form?.setValue("field_max", 100);
                form?.setValue("field_integer_places", 2);
                form?.setValue("field_decimal_places", 2);
                break;
            }
            case "date":
            case "duration_hours": {
                form?.setValue("field_date_options", {
                    year_only: false,
                    timezone: "client",
                    timeFormat: 'h:mma',
                });
                break;
            }
        }
    }
    addCrmModalLimitedSteps = [
        {   
            stepTitle: FetchAirtableDataStepTitle,
            stepComponent: FetchAirtableDataStep
        }
    ]
}

export const airtableCrmSystem = new AirtableCrmSystem();